/**
 * MindMapFlowContent - The main content component that consumes contexts
 *
 * This component exists separately from MindMapFlow because:
 * - MindMapFlow provides context providers (StakeholderProvider, LabelProvider, etc.)
 * - This component needs to consume those contexts
 * - React's rules prevent consuming a context in the same component that provides it
 */

import { useEffect, useRef, useMemo, useCallback } from 'react'
import * as Sentry from '@sentry/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ReactFlow, Background, useReactFlow, type Node } from '@xyflow/react'
import { useLabelFilterContext, useNodeLabelFiltering } from '../../features/labelFilter'
import { usePhaseAssignment } from './hooks/usePhaseAssignment'
import { useRatingsRegeneration } from './hooks/useRatingsRegeneration'
import { useStakeholderContext } from '../../features/stakeholder'
import { useLabelContext } from '../../context/label'
import { useThemeContext } from '../../context/theme'
import { useReadOnlyContext } from '../../context/readOnly'
import { useOptionalBranchSelectionContext } from '../../features/branchSelection'
import type { ReenterSelectionState } from '../../features/report'
import { useDataAccessContext } from '../../context/dataAccess'
import { NodeActionsProvider } from '../../context/nodeActions'
import { useChatWindowContext } from './Sidebars/ChatWindow/useChatWindowContext'
import { useActiveChatContext } from '../../context/activeChat'
import { ChatScopeChatWindow } from './Sidebars/ChatScopeChatWindow'
import { ReportSidebarFeature } from './Sidebars/ReportSidebar/ReportSidebarFeature'
import { MindMapHeader } from './Panels/MindMapHeader'
import * as LabelService from '../../services/labels/labelService'
// Primary color matching CSS --color-primary variable (light/dark)
const PRIMARY_COLOR = { light: '#6e53f9', dark: '#8a72fa' } as const
import { ContentWrapper, InnerContentContainer, ReactFlowWrapper } from './styles/MindMapFlowStyles'
import { nodeTypes, edgeTypes } from './config/reactFlowConfig'
import type { MindMapFlowContentProps } from './MindMapFlow.types'
import type { ReactFlowNodeData } from '../../types/mindmap-components'

// Phase assignment watcher - DISABLED
// Phase is now assigned by the backend during:
// 1. generateIdeas - assigns phase to parent node if missing before generating children
// 2. generateRatings - assigns phase to idea node if missing before rating
// This eliminates redundant API calls and token usage.
function PhaseAssignmentWatcher({ mindmapId }: { mindmapId: string | undefined }) {
  usePhaseAssignment({ mindmapId, enabled: false }) // DISABLED - backend handles it
  return null
}

// Ratings regeneration watcher - regenerates stakeholder ratings when idea content changes
// Note: regenerateFeedbackForChildren handles children, this handles the edited node itself
function RatingsRegenerationWatcher({ mindmapId }: { mindmapId: string | undefined }) {
  useRatingsRegeneration({ mindmapId, enabled: true })
  return null
}

export function MindMapFlowContent({
  getNodeLabelsRef,
  reactFlowWrapperRef,
  nodeActionsContextValue,
  mindmapId,
  mindmapTitle,
  mindmapImageStyle,
  onMindmapTitleChange,
  onMindmapImageStyleChange,
  nodes: styledNodes,
  styledEdges,
  reactFlowHandlers,
  onAddRootNode,
  stakeholderCountRef,
}: MindMapFlowContentProps) {
  // Destructure grouped props for easier access
  const {
    handleNodesChange,
    handleEdgesChange,
    handleConnect,
    onConnectEnd,
    handleNodeClick,
    handleNodeDragStart,
    handleNodeDrag,
    handleNodeDragStop,
    handleEdgeClick,
    handlePaneClick,
    onInit,
  } = reactFlowHandlers

  const { selectedFilterLabels } = useLabelFilterContext()
  const { reloadStakeholders, projectStakeholders } = useStakeholderContext()
  const { isReadOnly } = useReadOnlyContext()
  const branchSelection = useOptionalBranchSelectionContext()
  const { mode: dataAccessMode } = useDataAccessContext()
  const location = useLocation()
  const routerNavigate = useNavigate()

  // Re-enter branch selection mode when navigating back from another page (validate, report, etc.)
  const hasConsumedReenterState = useRef(false)
  useEffect(() => {
    const state = location.state as { reenterSelection?: ReenterSelectionState } | null
    if (!state?.reenterSelection || !branchSelection || hasConsumedReenterState.current) return

    hasConsumedReenterState.current = true
    const { nodeIds, storageKey, destination, title, subtitle } = state.reenterSelection

    // Clear the navigation state so browser back/forward doesn't re-trigger
    void routerNavigate(location.pathname, { replace: true, state: {} })

    // Defer activation to next tick so the state-clearing navigate settles first
    setTimeout(() => {
      branchSelection.activate({
        initialNodeIds: nodeIds,
        title,
        subtitle,
        onConfirm: (selectedIds: Set<string>) => {
          sessionStorage.setItem(storageKey, JSON.stringify([...selectedIds]))
          void routerNavigate(destination)
        },
      })
    }, 0)
  }, [location.state, branchSelection, routerNavigate, location.pathname])

  // Update stakeholder count ref for parent component (used to skip rating generation)
  useEffect(() => {
    // Using type assertion since RefObject is read-only but we need to update it
    ;(stakeholderCountRef as { current: number }).current = projectStakeholders.length
  }, [projectStakeholders.length, stakeholderCountRef])
  const { loadLabels, setLabelsDirectly, setNodeLabelsBatch, getNodeLabels, nodeLabelsVersion } =
    useLabelContext()
  const { theme } = useThemeContext()
  const hasLoadedStakeholdersRef = useRef<string | null>(null)
  const hasLoadedLabelsRef = useRef<string | null>(null)

  // Dynamic ReactFlow style based on theme
  const reactFlowStyle = useMemo(
    () => ({
      backgroundColor: theme === 'dark' ? '#141414ff' : '#f9fafaff',
      width: '100%',
      height: '100%',
    }),
    [theme]
  )

  // Prevent browser zoom globally on Ctrl+scroll / trackpad pinch.
  // Capture phase + non-passive so nothing downstream (React Flow, scrollable
  // child nodes calling stopPropagation, etc.) can bypass the guard.
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      // ctrlKey is true for both Ctrl+scroll and trackpad pinch-to-zoom
      if (e.ctrlKey) {
        e.preventDefault()
      }
    }

    document.addEventListener('wheel', handleWheel, { capture: true, passive: false })
    return () => document.removeEventListener('wheel', handleWheel, { capture: true })
  }, [])

  // Update the ref so parent component can access getNodeLabels
  useEffect(() => {
    getNodeLabelsRef.current = getNodeLabels
  }, [getNodeLabelsRef, getNodeLabels])

  // Label filtering - extracts ~100 lines of filtering logic to a dedicated hook
  const { nodesWithLabels, filteredEdges } = useNodeLabelFiltering({
    nodes: styledNodes,
    edges: styledEdges,
    selectedFilterLabels,
    getNodeLabels,
    nodeLabelsVersion,
  })

  // Load stakeholders when mindmapId changes (only once per mindmap)
  // Skip in read-only mode (public view) - stakeholders require authentication
  useEffect(() => {
    if (isReadOnly) return // Skip API calls in read-only/public view mode

    const currentRefValue = hasLoadedStakeholdersRef.current

    // Only load if we haven't already loaded stakeholders for this mindmap
    if (mindmapId && currentRefValue !== mindmapId) {
      hasLoadedStakeholdersRef.current = mindmapId
      reloadStakeholders(mindmapId).catch((err) => {
        console.error('❌ [MindMapFlowContent] Error loading stakeholders:', err)
        Sentry.captureException(err, {
          tags: { feature: 'mindmap', action: 'load_stakeholders' },
        })
        // Reset ref on error so we can retry
        hasLoadedStakeholdersRef.current = null
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mindmapId, isReadOnly]) // Only reload when mindmapId changes

  // Load labels and node labels when mindmapId changes
  // - Public view: use public endpoints (no authentication required)
  // - Archived or normal: use authenticated endpoints
  useEffect(() => {
    const currentRefValue = hasLoadedLabelsRef.current
    const cancellation: { cancelled: boolean } = { cancelled: false }

    // For public access, use public endpoints (no auth required)
    // For authenticated access (normal or archived), use authenticated endpoints
    const isPublicAccess = dataAccessMode === 'public'

    if (mindmapId && currentRefValue !== mindmapId && styledNodes.length > 0) {
      if (isPublicAccess) {
        // Public access mode: use public endpoints (no authentication required)
        const loadPublicLabels = async () => {
          try {
            // Load project labels and node labels in parallel
            const [labelsResult, nodeLabelsResult] = await Promise.all([
              LabelService.getPublicProjectLabels(mindmapId),
              LabelService.getPublicAllNodeLabels(mindmapId),
            ])
            if (cancellation.cancelled) return

            if (!labelsResult.error && labelsResult.data) {
              setLabelsDirectly(labelsResult.data.labels)
            }

            if (
              !nodeLabelsResult.error &&
              nodeLabelsResult.data &&
              nodeLabelsResult.data.nodeLabels.length > 0
            ) {
              setNodeLabelsBatch(nodeLabelsResult.data.nodeLabels)
            }

            hasLoadedLabelsRef.current = mindmapId
          } catch (err) {
            if (!cancellation.cancelled) {
              console.error('Error loading public labels:', err)
              Sentry.captureException(err, {
                tags: { feature: 'mindmap', action: 'load_public_labels' },
              })
            }
          }
        }
        void loadPublicLabels()
      } else {
        // Authenticated mode (normal or archived): use authenticated endpoints
        const nodeIds = styledNodes.map((n: Node) => n.id)

        // Load project labels first, then load all node labels in parallel
        loadLabels(mindmapId)
          .then(async () => {
            if (cancellation.cancelled) return

            try {
              // Load all node labels in parallel
              const labelResults = await Promise.all(
                nodeIds.map(async (nodeId: string) => {
                  const { data, error } = await LabelService.getNodeLabels(mindmapId, nodeId)
                  return { nodeId, labels: !error && data ? data.labels : [] }
                })
              )

              // Filter to only nodes with labels and batch update all at once
              const updates = labelResults.filter((result) => result.labels.length > 0)
              if (updates.length > 0) {
                setNodeLabelsBatch(updates)
              }

              // Only mark as loaded AFTER successful completion (not before async ops)
              // This fixes StrictMode double-invocation where first run sets ref but gets cancelled
              hasLoadedLabelsRef.current = mindmapId
            } catch (err) {
              console.error('Error loading node labels:', err)
              Sentry.captureException(err, {
                tags: { feature: 'mindmap', action: 'load_node_labels' },
              })
            }
          })
          .catch((err) => {
            if (!cancellation.cancelled) {
              console.error('Error loading labels:', err)
              Sentry.captureException(err, {
                tags: { feature: 'mindmap', action: 'load_labels' },
              })
            }
          })
      }
    }

    return () => {
      cancellation.cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mindmapId, styledNodes.length, dataAccessMode]) // Reload when mindmapId changes or nodes are loaded

  // Get chat window context for rendering the chat window
  const chatWindow = useChatWindowContext()

  // Active chat — close node chat on pane click or click of a different node
  const { activeChatNodeId, closeChat } = useActiveChatContext()

  // Get ReactFlow instance for navigating to nodes from report sidebar
  const { fitView, setNodes } = useReactFlow()

  // Handler for clicking nodes in the report sidebar - centers view on the node
  const handleReportNodeClick = useCallback(
    (nodeId: string) => {
      // Mark the node as selected
      setNodes((nodes) =>
        nodes.map((n) => ({
          ...n,
          selected: n.id === nodeId,
        }))
      )

      // Center view on the node
      setTimeout(() => {
        void fitView({
          nodes: [{ id: nodeId }],
          duration: 500,
          padding: 0.5,
          maxZoom: 1,
        })
      }, 50)
    },
    [fitView, setNodes]
  )

  // Branch selection: intercept node clicks to toggle branch selection
  const branchSelectionIsActive = branchSelection?.isActive ?? false
  const branchSelectionToggle = branchSelection?.toggleBranch
  const handleNodeClickWithBranchSelection = useCallback(
    (event: React.MouseEvent, node: Node) => {
      // Close chat if a different node is clicked
      if (activeChatNodeId && activeChatNodeId !== node.id) {
        closeChat()
      }
      if (branchSelectionIsActive && branchSelectionToggle) {
        event.stopPropagation()
        branchSelectionToggle(node.id)
        return
      }
      handleNodeClick(event, node as Node<ReactFlowNodeData>)
    },
    [activeChatNodeId, closeChat, branchSelectionIsActive, branchSelectionToggle, handleNodeClick]
  )

  // Branch selection: apply className to nodes for visual highlighting
  const finalNodes = useMemo(() => {
    if (!branchSelection?.isActive) return nodesWithLabels
    return nodesWithLabels.map((node) => ({
      ...node,
      className: branchSelection.selectedNodeIds.has(node.id)
        ? 'branch-selected'
        : 'branch-unselected',
    }))
  }, [nodesWithLabels, branchSelection?.isActive, branchSelection?.selectedNodeIds])

  // Branch selection: color edges between two selected nodes with primary color
  const primaryColor = theme === 'dark' ? PRIMARY_COLOR.dark : PRIMARY_COLOR.light
  const finalEdges = useMemo(() => {
    if (!branchSelection?.isActive) return filteredEdges
    const selected = branchSelection.selectedNodeIds
    return filteredEdges.map((edge) => {
      const bothSelected = selected.has(edge.source) && selected.has(edge.target)
      if (!bothSelected) return edge
      return {
        ...edge,
        data: {
          ...edge.data,
          colorOverride: primaryColor,
        },
      }
    })
  }, [filteredEdges, branchSelection?.isActive, branchSelection?.selectedNodeIds, primaryColor])

  // Show research button only when the mindmap has content beyond the root venture node
  const showResearchButton = useMemo(
    () => nodesWithLabels.some((n) => n.data.category !== 'venture'),
    [nodesWithLabels]
  )

  return (
    <NodeActionsProvider value={nodeActionsContextValue}>
      <ReportSidebarFeature onNodeClick={handleReportNodeClick}>
        <ContentWrapper>
          <InnerContentContainer>
            <ReactFlowWrapper ref={reactFlowWrapperRef}>
              {/* Header with filter, export, add buttons */}
              {/* Save state is provided via SaveStateContext */}
              <MindMapHeader
                isEmpty={nodesWithLabels.length === 0}
                showResearchButton={showResearchButton}
                onAddNode={onAddRootNode}
                mindmapId={mindmapId}
                mindmapTitle={mindmapTitle}
                mindmapImageStyle={mindmapImageStyle}
                onMindmapTitleChange={onMindmapTitleChange}
                onMindmapImageStyleChange={onMindmapImageStyleChange}
              />
              <ReactFlow
                key={`mindmap-${mindmapId}`}
                nodes={finalNodes}
                edges={finalEdges}
                onNodesChange={handleNodesChange}
                onEdgesChange={handleEdgesChange}
                onConnect={isReadOnly ? undefined : handleConnect}
                onConnectEnd={isReadOnly ? undefined : onConnectEnd}
                onNodeClick={handleNodeClickWithBranchSelection}
                onNodeDragStart={isReadOnly ? undefined : handleNodeDragStart}
                onNodeDrag={isReadOnly ? undefined : handleNodeDrag}
                onNodeDragStop={isReadOnly ? undefined : handleNodeDragStop}
                onEdgeClick={handleEdgeClick}
                onPaneClick={() => {
                  closeChat()
                  handlePaneClick()
                }}
                onInit={onInit}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                onlyRenderVisibleElements={false}
                fitView
                fitViewOptions={{ maxZoom: 1.2 }}
                minZoom={0.1}
                maxZoom={1.5}
                deleteKeyCode={isReadOnly ? null : ['Delete', 'Backspace']}
                multiSelectionKeyCode={null}
                selectionKeyCode={null}
                nodesDraggable={!isReadOnly}
                nodesConnectable={!isReadOnly}
                edgesReconnectable={!isReadOnly}
                elementsSelectable={true}
                panOnDrag={true}
                selectNodesOnDrag={false}
                panOnScroll={true}
                panOnScrollSpeed={1.5}
                zoomOnScroll={false}
                colorMode={theme === 'dark' ? 'dark' : 'light'}
                style={reactFlowStyle}
                proOptions={{ hideAttribution: true }}
              >
                <Background />
                <PhaseAssignmentWatcher mindmapId={mindmapId} />
                <RatingsRegenerationWatcher mindmapId={mindmapId} />
              </ReactFlow>
            </ReactFlowWrapper>

            {/* Report Sidebar - rendered by ReportSidebarFeature wrapper */}
          </InnerContentContainer>

          {/* Chat Window */}
          <ChatScopeChatWindow
            isVisible={chatWindow.isChatVisible}
            onClose={chatWindow.closeChat}
            onSendMessage={chatWindow.sendMessage}
            messages={chatWindow.chatMessages}
            isLoading={chatWindow.isChatLoading}
            stakeholderName={chatWindow.stakeholderName}
            stakeholderRole={chatWindow.stakeholderRole}
            feedbackContext={chatWindow.feedbackContext}
          />
        </ContentWrapper>
      </ReportSidebarFeature>
    </NodeActionsProvider>
  )
}
